import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  Search,
  Download,
  Eye,
  Users,
  Wallet,
  ShoppingBag,
  UserCheck,
} from 'lucide-react';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-50 text-green-600 border border-green-200' },
  inactive: { label: 'Inactive', color: 'bg-gray-50 text-gray-500 border border-gray-200' },
  pending: { label: 'Pending', color: 'bg-yellow-50 text-yellow-600 border border-yellow-200' },
  suspended: { label: 'Suspended', color: 'bg-red-50 text-red-500 border border-red-200' },
};

const KYC_CONFIG = {
  approved: { label: 'Verified', color: 'bg-green-50 text-green-600 border border-green-200' },
  pending_review: { label: 'Pending Review', color: 'bg-amber-50 text-amber-600 border border-amber-200' },
  pending: { label: 'Not Submitted', color: 'bg-gray-50 text-gray-500 border border-gray-200' },
  rejected: { label: 'Rejected', color: 'bg-red-50 text-red-500 border border-red-200' },
};

const AVATAR_COLORS = [
  '#F472B6', '#A78BFA', '#60A5FA', '#34D399', '#FBBF24', '#FB7185', '#818CF8', '#2DD4BF'
];

const getAvatarColor = (name) => {
  const index = (name?.charCodeAt(0) || 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

const AdminCustomerManagement = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [kycFilter, setKycFilter] = useState('all');

  useEffect(() => {
    loadCustomers();
  }, [search, statusFilter]);

  const loadCustomers = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await api.get(`/customers?${params.toString()}`);
      let data = res.data;
      if (kycFilter !== 'all') {
        data = data.filter(c => {
          if (kycFilter === 'pending_review') return c.kyc_status === 'pending' && c.kyc_document;
          if (kycFilter === 'pending') return c.kyc_status === 'pending' && !c.kyc_document;
          return c.kyc_status === kycFilter;
        });
      }
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      loadCustomers();
    }
  }, [kycFilter]);

  const handleExport = () => {
    const csvContent = [
      ['Customer No', 'Full Name', 'Email', 'Phone', 'IC Number', 'KYC Status', 'Status', 'Wallet Balance', 'Joined'].join(','),
      ...customers.map(c => [
        c.customer_number || '-',
        c.full_name,
        c.email,
        c.phone,
        c.ic_number || '-',
        c.kyc_status || 'pending',
        c.status || 'active',
        c.wallet_balance || 0,
        c.created_at?.split('T')[0] || '-'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customers_export.csv';
    a.click();
    toast.success('Export completed');
  };

  const summary = {
    total: customers.length,
    active: customers.filter(c => (c.status || 'active') === 'active').length,
    verified: customers.filter(c => c.kyc_status === 'verified').length,
    totalBalance: customers.reduce((sum, c) => sum + (c.wallet_balance || 0), 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="customer-loading">
        <div className="animate-spin w-8 h-8 border-2 border-[#4E50FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="customer-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage all registered customers</p>
        </div>
        <Button onClick={handleExport} variant="outline" data-testid="export-customers-btn">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Total Customers</p>
            <p className="text-xl font-bold text-gray-900" data-testid="total-customers">{summary.total}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
            <UserCheck className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Active</p>
            <p className="text-xl font-bold text-gray-900">{summary.active}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
            <ShoppingBag className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">KYC Verified</p>
            <p className="text-xl font-bold text-gray-900">{summary.verified}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Total Wallet Balance</p>
            <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.totalBalance)}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name, email, phone, customer no, or IC number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="customer-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
            </SelectContent>
          </Select>
          <Select value={kycFilter} onValueChange={setKycFilter}>
            <SelectTrigger className="w-40" data-testid="kyc-filter">
              <SelectValue placeholder="KYC Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All KYC</SelectItem>
              <SelectItem value="approved">Verified</SelectItem>
              <SelectItem value="pending_review">Pending Review</SelectItem>
              <SelectItem value="pending">Not Submitted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50/50">
              <TableHead className="font-semibold text-gray-600">Customer</TableHead>
              <TableHead className="font-semibold text-gray-600">Customer No.</TableHead>
              <TableHead className="font-semibold text-gray-600">Phone</TableHead>
              <TableHead className="font-semibold text-gray-600">IC Number</TableHead>
              <TableHead className="font-semibold text-gray-600">KYC</TableHead>
              <TableHead className="font-semibold text-gray-600">Wallet</TableHead>
              <TableHead className="font-semibold text-gray-600">Status</TableHead>
              <TableHead className="font-semibold text-gray-600">Joined</TableHead>
              <TableHead className="font-semibold text-gray-600 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-gray-400">
                  No customers found
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer) => {
                const avatarColor = getAvatarColor(customer.full_name);
                const statusCfg = STATUS_CONFIG[customer.status || 'active'] || STATUS_CONFIG.active;
                // Determine KYC display status - differentiate between not submitted and pending review
                const effectiveKyc = (customer.kyc_status === 'pending' && customer.kyc_document)
                  ? 'pending_review'
                  : customer.kyc_status;
                const kycCfg = KYC_CONFIG[effectiveKyc] || KYC_CONFIG.pending;
                return (
                  <TableRow
                    key={customer.id}
                    className="hover:bg-gray-50/50 cursor-pointer"
                    onClick={() => navigate(`/admin/customers/${customer.id}`)}
                    data-testid={`customer-row-${customer.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                          style={{ backgroundColor: avatarColor }}
                        >
                          {customer.full_name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{customer.full_name}</p>
                          <p className="text-xs text-gray-500">{customer.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-mono text-[#4E50FF] font-medium">
                          {customer.customer_number || '-'}
                        </span>
                        {customer.user_code && (
                          <span className="text-xs font-mono text-gray-500">
                            {customer.user_code}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">{customer.phone || '-'}</TableCell>
                    <TableCell className="text-sm text-gray-600">{customer.ic_number || '-'}</TableCell>
                    <TableCell>
                      <Badge className={`${kycCfg.color} font-medium px-2 py-0.5 rounded-full text-xs`}>
                        {kycCfg.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm font-medium text-gray-900">
                      {formatCurrency(customer.wallet_balance || 0)}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${statusCfg.color} font-medium px-2 py-0.5 rounded-full text-xs`}>
                        {statusCfg.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {customer.created_at ? formatDate(customer.created_at) : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); navigate(`/admin/customers/${customer.id}`); }}
                        data-testid={`view-customer-${customer.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AdminCustomerManagement;
